<script setup lang="ts">
import { computed, onMounted, provide, ref, toRefs, watch } from 'vue';
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import { useRoute, useRouter } from 'vue-router';
import { SUREbusinessCommonDomainRelatiesSoortRelatieEnum } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRelatiesSoortRelatieEnum';
import { useRelatieSpecialeContactpersonenWijzigSpecialeContactpersonen } from '@services/crm-public/orval-api/relatie-speciale-contactpersonen-api/relatie-speciale-contactpersonen-api';
import {
  useRelatiesGetRelationByRelatienummer,
  useRelatiesMuteerRelatieById,
} from '@services/crm-public/orval-api/relaties-api/relaties-api';
import { CrmRelationsRelatieContext } from '@services/crm-public/orval-api/model/crmRelationsRelatieContext';
import { type SUREbusinessCommonDomainIncassocode as Incassocode } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainIncassocode';
import { type SUREbusinessCommonDomainRechtsvormEnum as Rechtsvorm } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRechtsvormEnum';
import { type SUREbusinessCommonDomainRelatiesTitulatuurcodeType as Titelatuurcode } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRelatiesTitulatuurcodeType';
import { usePolisGetPolissen } from '@services/polis-public/orval-api/polis-api/polis-api';
import { createErrorMessage } from '@errors/helpers';
import { VButton } from '@shared/component/VButton';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import { useLocalStorage, useMagicKeys, whenever } from '@vueuse/core';
import { useForm } from 'vee-validate';
import { isZakelijkeRelatie, type Relatie, Relatietype } from '@relatie-lib/relatieGegevens/types';
import { isDisabledInjectionKey } from '@relatie-lib/relatieGegevens/relatiegegevensForm/keys';
import { mapToDto } from '@relatie-lib/relatieGegevens/helper';
import { maakLegeRelatie } from '@relatie-lib/relatieGegevens/relatie';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useQueryClient } from '@tanstack/vue-query';
import { hasPermission } from '@permission/hasPermission';
import { useClaims } from '@auth/useClaims';
import { deepClone } from '@tools/helpers';
import { toDate } from '@tools/dates';
import { useErrorHandler } from '@errors/useErrorHandler';
import RelatiegegevensHistorieModal from './modals/RelatiegegevensHistorieModal.vue';
import ZakelijkeRelatieForm from '../../relaties/ZakelijkeRelatieForm.vue';
import ParticuliereRelatieForm from '../../relaties/ParticuliereRelatieForm.vue';
import ContactpersoonLayout from '../../relaties/contactpersonen/ContactpersonenLayout.vue';
import { isParticuliereRelatieDto, isZakelijkeRelatieDto } from '../relatiegegevensUtils';
import { type ContactpersoonEditMode } from '../../relaties/contactpersonen/types';
import { useKvkBedrijfsProfiel } from '../useKvkBedrijfsProfiel';

const props = defineProps<{
  relatienummer: number;
}>();
const { relatienummer } = toRefs(props);
const { handleError } = useErrorHandler();
const claims = useClaims();
const keys = useMagicKeys();

const route = useRoute();
const router = useRouter();

const notification = useNotification();

const queryClient = useQueryClient();

const disabled = ref<boolean>(false);
provide(isDisabledInjectionKey, { value: disabled });

const magBijwerken = hasPermission('Crm.RelatieBijwerken');

const {
  queryKey,
  isPending,
  isError,
  error,
  data: relatie,
} = useRelatiesGetRelationByRelatienummer(relatienummer, {
  query: {
    select: (r) => r.result,
    retry: 3,
    staleTime: 5 * 60 * 1000,
  },
});

const { data: pakketpolissen } = usePolisGetPolissen(
  computed(() => ({
    relatienummer: relatienummer.value,
    neemRoyementenMee: true,
    neemBeursPakkettenMee: true,
  })),
  {
    query: {
      select: (result) => result.result,
      retry: 3,
      staleTime: 5 * 60 * 1000,
    },
  },
);

const isInEditMode = ref<boolean>(false);
const isHistorieOpen = ref<boolean>(false);

function onError(err: any) {
  handleError(err);
  disabled.value = false;
}

const { mutate: muteerRelatie, isPending: isMuteerRelatieSubmitting } =
  useRelatiesMuteerRelatieById({
    mutation: {
      onError,
      onSuccess: onMuteerRelatieSuccess,
    },
  });
const { mutate: muteerSpecialeContactpersonen, isPending: isSpecialeContactpersonenSubmitting } =
  useRelatieSpecialeContactpersonenWijzigSpecialeContactpersonen({
    mutation: {
      onError,
      onSuccess,
    },
  });
const isSubmitting = computed(
  () => isMuteerRelatieSubmitting.value || isSpecialeContactpersonenSubmitting.value,
);

const debug = useLocalStorage('CRM_APP_DEBUG', false);

whenever(keys.alt_w, () => {
  if (claims.value?.isInternal) debug.value = !debug.value;
});

const isZakelijk = computed(
  () => relatie.value?.typeName === SUREbusinessCommonDomainRelatiesSoortRelatieEnum.Z,
);
const isParticulier = computed(
  () => relatie.value?.typeName === SUREbusinessCommonDomainRelatiesSoortRelatieEnum.P,
);

const { errors, values, setValues, resetForm, handleSubmit, setFieldValue } = useForm<Relatie>();

const zijnAfwijkendeEmailadressenAangepast = computed(
  () => values.financieelEmailadres !== relatie.value?.financieelEmailadres,
);

function onMuteerRelatieSuccess() {
  if (zijnAfwijkendeEmailadressenAangepast.value) {
    muteerSpecialeContactpersonen({
      relatienummer: relatienummer.value,
      data: {
        financieelEmailadres: values.financieelEmailadres,
      },
    });
  } else {
    onSuccess();
  }
}

function onSuccess() {
  disabled.value = false;
  resetForm({ values: deepClone(values) });
  isInEditMode.value = false;
  queryClient.invalidateQueries({ queryKey });
  notification.success('Wijzigingen opgeslagen.');
}

const { bedrijfsProfiel } = useKvkBedrijfsProfiel(values, relatie);

watch(bedrijfsProfiel, (bedrijf) => {
  setFieldValue('adres.postcode', bedrijf?.adres.postcode);
  setFieldValue('adres.huisnummer', bedrijf?.adres.huisnummer);
  setFieldValue('adres.huisnummertoevoeging', bedrijf?.adres.huisnummertoevoeging);
  setFieldValue('bedrijfsnaam', bedrijf?.naam);
  setFieldValue('sbicode', bedrijf?.sbicode);
  setFieldValue('rechtsvorm', bedrijf?.rechtsvorm);
});

const toonBedrijfseigenaar = computed(
  () =>
    !!pakketpolissen.value?.length &&
    isZakelijkeRelatie(values) &&
    relatie.value?.name !== values.bedrijfsnaam,
);

watch(isInEditMode, (val) => {
  if (!val) resetForm();
});

const submit = handleSubmit(() => {
  disabled.value = true;
  muteerRelatie({
    relatienummer: relatienummer.value,
    data: mapToDto(values, CrmRelationsRelatieContext.Algemeen),
  });
});

const contactpersonen = ref<Array<ContactpersoonEditMode>>([]);

watch(
  () => relatie.value,
  (r) => {
    if (!r) return;
    let rel: Relatie = maakLegeRelatie();
    const commonFields = {
      relatieId: r.id,
      automatischeIncassoAkkoord: true,
      relatienummer: r.relationId,
      email: r.emailadres ?? '',
      telefoonnummer: r.telefoonnummer,
      incasso: (r.incassocode ?? undefined) as Incassocode | undefined,
      adres: {
        plaats: r.adres?.plaats,
        huisnummer: r.adres?.huisnummer,
        huisnummertoevoeging: r.adres?.huisnummertoevoeging,
        postcode: r.adres?.postcode,
        straat: r.adres?.straat,
      },
      rekening: r.rekening
        ? { id: r.rekening.id, iban: r.rekening.rekeningnummer, bic: r.rekening.bic }
        : undefined,
      financieelEmailadres: r.financieelEmailadres,
    };

    if (isZakelijkeRelatieDto(r)) {
      rel = {
        ...commonFields,
        sbicode: r.sbiCode?.valueStripped,
        vestigingsnummer: r.vestigingsnummer,
        bedrijfsnaam: r.name,
        rechtsvorm: r.rechtsvorm as Rechtsvorm | undefined,
        kvknummer: r.kvknummer,
        website: r.website,
        typeName: Relatietype.Z,
      };
      contactpersonen.value = r.contactpersonen.map((c) => ({
        ...c,
        titulatuurcode: c.titulatuurcode as Titelatuurcode,
        geboortedatum: c.geboortedatum ? toDate(c.geboortedatum) : undefined,
        inEditMode: false,
        isLoading: false,
      }));
    }

    if (isParticuliereRelatieDto(r)) {
      rel = {
        ...commonFields,
        achternaam: r.achternaam ?? undefined,
        geboortedatum: r.geboortedatum ? new Date(Date.parse(r.geboortedatum)) : undefined,
        voorletters: r.voorletters ?? undefined,
        tussenvoegsels: r.tussenvoegsels ?? undefined,
        titulatuurcode: r.titulatuurcode as Titelatuurcode | undefined,
        typeName: Relatietype.P,
      };
    }
    setValues(rel);
  },
  { immediate: true },
);

onMounted(() => {
  // client side redirect omdat de hash niet naar de server wordt gezonden
  if (route.hash.includes('naselectie')) router.push({ name: 'naselectie' });
});

const kanOpslaan = computed(() => !Object.keys(errors.value ?? {}).length);
</script>

<template>
  <VPageContainer container-size="sm" title="Algemeen">
    <template #actions>
      <VButton
        prepend-icon="far fa-timeline"
        icon-only-responsiveness
        class="mb-4"
        inverted
        @click="isHistorieOpen = true"
      >
        Historie
      </VButton>
    </template>

    <VInfoBox badge-text="error" type="danger" v-if="isError">
      <div v-html="createErrorMessage(error)"></div>
    </VInfoBox>
    <div class="flex flex-col gap-8">
      <VInfoBox badge-text="Debug: Form values" v-if="debug">
        <span class="font-mono whitespace-pre">{{ values }}</span>
      </VInfoBox>
      <VInfoBox badge-text="Debug: API response" v-if="debug">
        <span class="font-mono whitespace-pre">{{ relatie }}</span>
      </VInfoBox>
      <ZakelijkeRelatieForm
        :loading="isPending"
        :is-submitting
        :kan-opslaan="kanOpslaan"
        :kan-bewerken="magBijwerken"
        :toon-bedrijfseigenaar="toonBedrijfseigenaar"
        v-model="isInEditMode"
        @save="submit"
        :relatienummer
        v-if="isZakelijk"
      />
      <ParticuliereRelatieForm
        :loading="isPending"
        :is-submitting
        :kan-opslaan="kanOpslaan"
        :kan-bewerken="magBijwerken"
        v-model="isInEditMode"
        @save="submit"
        :relatienummer
        v-if="isParticulier"
      />
      <ContactpersoonLayout v-model="contactpersonen" v-if="isZakelijk" :relatienummer />
    </div>
    <RelatiegegevensHistorieModal
      :relatienummer
      titel="Historie"
      omschrijving="Alle wijzigingen aan de relatie en de bijbehorende contactpersonen verschijnen hier."
      :filter="(log) => log.type !== 'Ubo'"
      v-model:is-open="isHistorieOpen"
      v-if="isHistorieOpen"
    />
  </VPageContainer>
</template>
